// src/screens/Users.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/Dashboard.css";
import "../css/users.css";
/* import Settings styles so the action button colors match exactly */
import "../css/Settings.css";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import useIdleLogout from "../hooks/useIdleLogout";

/* ================== Env-only config ================== */
const API_USERS     = (process.env.REACT_APP_USERS_API_URL     || "").trim();
const API_DRIVERS   = (process.env.REACT_APP_DRIVERS_API_URL   || "").trim();
const API_MERCHANTS = (process.env.REACT_APP_MERCHANTS_API_URL || "").trim();
const BASEURL_ADMIN = (process.env.REACT_APP_BASEURL_ADMIN     || "").trim();
const EVENTS_BASE   = (process.env.REACT_APP_EVENTS_API_URL    || "").replace(/\/+$/, "");
const API_EVENTS_ORGANIZERS = EVENTS_BASE ? `${EVENTS_BASE}/organizers` : "";

/* ---- Admin action endpoints ---- */
const API_ADMIN_ACTIVATE = (
  process.env.REACT_APP_ADMIN_ACTIVATE_API_URL || ""
).trim(); // .../activate/:user_id
const API_ADMIN_DEACTIVATE = (
  process.env.REACT_APP_ADMIN_DEACTIVATE_API_URL || ""
).trim(); // .../deactivate/:user_id

/* ---- Admin logs (POST) ---- */
const API_LOGS_CREATE_BASE = (
  process.env.REACT_APP_ADMIN_LOGS_CREATE_API_URL ||
  process.env.REACT_APP_ADMIN_LOGS_API_URL ||
  ""
).trim();

/* ---- User notifications (single) ---- */
const API_NOTIFY_BASE = (
  process.env.REACT_APP_DUE_SETTLEMENT_NOTIFY_API_BASE || ""
).trim(); // .../system-notifications/user
const API_NOTIFY_LOGS_BASE = (
  process.env.REACT_APP_DUE_SETTLEMENT_NOTIFY_LOGS_API_URL || ""
).trim(); // .../system-notifications/user/logs

/* page size: 20 rows per table */
const PAGE_SIZE = 20;

/* ============ Default avatar image ============ */
/* Uses the same placeholder image style as your admin profile page */
const DEFAULT_AVATAR =
  process.env.PUBLIC_URL + "/assets/img/admin profile.jpg";

/* ================== UI bits ================== */
function StatusPill({ verified }) {
  return (
    <span className={`status-pill ${verified ? "on" : "off"}`}>
      {verified ? "Online" : "Offline"}
    </span>
  );
}

function resolveImageSrc(src) {
  if (!src) return "";
  const s = String(src).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!BASEURL_ADMIN) return s;
  const base = BASEURL_ADMIN.replace(/\/+$/, "");
  const path = s.replace(/^\/+/, "");
  return `${base}/${path}`;
}

function Avatar({ src, name }) {
  const resolved = resolveImageSrc(src);
  const finalSrc = resolved || DEFAULT_AVATAR;

  return (
    <img
      className={`avatar${resolved ? "" : " avatar-placeholder"}`}
      src={finalSrc}
      alt={name || "avatar"}
      onError={(e) => {
        // if real image fails, fall back to default placeholder once
        if (e.currentTarget.src !== DEFAULT_AVATAR) {
          e.currentTarget.onerror = null;
          e.currentTarget.src = DEFAULT_AVATAR;
        }
      }}
    />
  );
}

/* Reusable table card */
function TableCard({
  title,
  columns = [],
  rows = [],
  loading,
  emptyText = "No data",
  page = 1,
  pageSize = PAGE_SIZE,
  total = 0,
  onPageChange = () => {},
  scrollX = false,
  tableMinWidth,
}) {
  const scrollStyle = scrollX ? { overflowX: "auto" } : undefined;
  const tableStyle = scrollX
    ? { minWidth: tableMinWidth || 1600 }
    : undefined;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="card table-card users-stack-item">
      <h5>
        {title}
        {total > 0 && (
          <span className="users-total-pill">{total.toLocaleString()}</span>
        )}
      </h5>

      {/* custom scroll wrapper to avoid Bootstrap conflicts */}
      <div className="users-scroll" style={scrollStyle}>
        <table className="table users-table" style={tableStyle}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key || c.header}>{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="muted">
                  Loading…
                </td>
              </tr>
            ) : rows && rows.length ? (
              rows.map((r, i) => (
                <tr key={r.id || r.user_id || r.email || `${title}-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key || c.header}>
                      {typeof c.render === "function"
                        ? c.render(r, i)
                        : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="muted">
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="table-pager">
          <div className="pager-left">
            <small>
              {from}–{to} of {total.toLocaleString()}
            </small>
          </div>
          <div className="pager-right">
            <button
              className="pager-btn"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              ‹ Prev
            </button>
            <span className="pager-sep" />
            <small>{page} / {totalPages}</small>
            <span className="pager-sep" />
            <button
              className="pager-btn"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================== Helpers ================== */
const norm = (v) => String(v ?? "").toLowerCase();

/* format average rating like 4.5, show — if empty/0 */
function formatRating(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return n.toFixed(1).replace(/\.0$/, "");
}

/* read rating from either avg_rating or average_rating */
function getAverageRating(row) {
  return formatRating(row.avg_rating ?? row.average_rating);
}

/* ---- Wallet ID resolver (supports multiple API shapes) ---- */
function getWalletId(r) {
  const direct =
    r.wallet_id ?? r.walletId ?? r.walletid ?? r.user_wallet_id ?? r.admin_wallet_id;
  if (direct) return String(direct);

  const nested =
    r.wallet?.wallet_id ??
    r.wallet?.id ??
    r.profile?.wallet_id ??
    r.meta?.wallet_id;
  if (nested) return String(nested);

  const alt =
    r.business?.wallet_id ?? r.user?.wallet_id ?? r.account?.wallet_id;
  if (alt) return String(alt);

  return "—";
}

/* ---- Points collected resolver (for General Users) ---- */
function getPointsCollected(r) {
  const candidates = [
    r.points_collected,
    r.points,
    r.total_points,
    r.collected_points,
    r.reward_points,
    r.loyalty_points,
  ];
  const val = candidates.find((v) => v !== undefined && v !== null);
  if (val === undefined || val === null) return "0";
  const n = Number(val);
  if (Number.isFinite(n)) return String(n);
  return String(val);
}

/**
 * Updated filterRows:
 * - Includes Wallet ID, Average Rating, Online/Offline, Active/Inactive
 * - Includes vehicle info for drivers and business name for merchants
 * So search bar now works for wallet id + all visible columns.
 */
function filterRows(rows, q, type) {
  if (!q) return rows;
  const needle = norm(q);

  return (rows || []).filter((r) => {
    const walletId = getWalletId(r);
    const avgRating = getAverageRating(r);
    const statusText = r.is_verified ? "online" : "offline";
    const activeText = r.is_active ? "active" : "inactive";

    const parts = [
      r.user_name,
      r.name,
      r.email,
      r.phone,
      r.business_name,
      r.license_number,
      walletId,
      avgRating,
      statusText,
      activeText,
    ];

    if (type === "drivers") {
      const v = (r.vehicles && r.vehicles[0]) || {};
      parts.push(v.make, v.color, v.license_plate, v.vehicle_type);
    } else if (type === "merchants") {
      parts.push(r.owner_type);
    }

    const haystack = parts.map(norm).join(" ");
    return haystack.includes(needle);
  });
}

function stamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(
    d.getDate()
  )}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}
function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}
function longDateTime() {
  const d = new Date();
  const day = d.toLocaleDateString(undefined, { weekday: "long" });
  const date = d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  // fixed template literal
  return `${day}, ${date} ${time}`;
}

function readAuthedUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function getAdminName() {
  const u = readAuthedUser() || {};
  const firstLast = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return u.full_name || u.user_name || u.name || firstLast || "Admin";
}
function getAdminId() {
  const u = readAuthedUser() || {};
  return u.id ?? u.user_id ?? u.admin_id ?? null;
}

/* ---- Smart fetch for ALL rows ---- */
function buildUrl(url, params = {}) {
  const u = new URL(url, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") u.searchParams.set(k, v);
  });
  return u.toString();
}

async function fetchAllRows(
  url,
  { pageParam = "page", sizeParam = "per_page", bigPageSize = 10000 } = {}
) {
  let res = await fetch(
    buildUrl(url, { [sizeParam]: bigPageSize, [pageParam]: 1 })
  );
  let json = await res.json();
  let arr = Array.isArray(json)
    ? json
    : Array.isArray(json?.data)
    ? json.data
    : [];
  const meta = json?.meta || {};
  const total = meta.total ?? json?.total ?? null;

  if (!total || arr.length >= total || arr.length < bigPageSize) return arr;

  let all = arr.slice();
  let page = 2;
  const maxPages = 200;
  while (all.length < total && page <= maxPages) {
    res = await fetch(
      buildUrl(url, { [sizeParam]: bigPageSize, [pageParam]: page })
    );
    json = await res.json();
    const pageArr = Array.isArray(json)
      ? json
      : Array.isArray(json?.data)
      ? json.data
      : [];
    if (!pageArr.length) break;
    all = all.concat(pageArr);
    if (pageArr.length < bigPageSize) break;
    page++;
  }
  return all;
}

/* -------- PDF helper -------- */
function exportTableToPDF({ title, columns, rows }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const left = 40;
  const top = 48;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(title, left, top);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Exported: ${longDateTime()}`, left, top + 18);

  const head = [columns.map((c) => c.header)];
  const body = rows.map((r, idx) =>
    columns.map((c) => (c.getter ? c.getter(r, idx) : r[c.key] ?? ""))
  );

  autoTable(doc, {
    head,
    body,
    startY: top + 28,
    margin: { left, right: 40, bottom: 40 },
    styles: { fontSize: 9, cellPadding: 6, overflow: "linebreak" },
    headStyles: { fillColor: [13, 110, 253], textColor: 255, fontStyle: "bold" },
    didDrawPage: (data) => {
      const pageSize = doc.internal.pageSize;
      const pageNum = doc.internal.getNumberOfPages();
      const pageStr = `Page ${data.pageNumber} of ${pageNum}`;
      doc.setFontSize(9);
      doc.text(pageStr, pageSize.getWidth() - 40, pageSize.getHeight() - 20, {
        align: "right",
      });
    },
  });

  doc.save(`${slug(title)}_${stamp()}.pdf`);
}

/* ================== Page ================== */
export default function Users() {
  useIdleLogout();

  const [query, setQuery] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportWrapRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errorUsers, setErrorUsers] = useState("");

  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [errorDrivers, setErrorDrivers] = useState("");

  const [usersTotal, setUsersTotal] = useState(0);
  const [driversTotal, setDriversTotal] = useState(0);

  const [merchantsFood, setMerchantsFood] = useState([]);
  const [merchantsMart, setMerchantsMart] = useState([]);
  const [merchantsEvents, setMerchantsEvents] = useState([]);
  const [merchantsDining, setMerchantsDining] = useState([]);
  const [merchantsHotel, setMerchantsHotel] = useState([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [errorMerchants, setErrorMerchants] = useState("");

  const [eventOrganizers, setEventOrganizers] = useState([]);
  const [loadingOrganizers, setLoadingOrganizers] = useState(false);

  const [driverPage, setDriverPage] = useState(1);
  const [eventOrganizersPage, setEventOrganizersPage] = useState(1);
  const [merchFoodPage, setMerchFoodPage] = useState(1);
  const [merchMartPage, setMerchMartPage] = useState(1);
  const [merchEventsPage, setMerchEventsPage] = useState(1);
  const [merchDiningPage, setMerchDiningPage] = useState(1);
  const [merchHotelPage, setMerchHotelPage] = useState(1);
  const [customerPage, setCustomerPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);

  useEffect(() => {
    function onDocClick(e) {
      if (exportWrapRef.current && !exportWrapRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const loadUsers = (page = 1) => {
    setLoadingUsers(true);
    fetch(buildUrl(API_USERS, { page, per_page: PAGE_SIZE }))
      .then((r) => r.json())
      .then((data) => {
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        setUsers(arr);
        const t = data?.meta?.total ?? data?.total ?? arr.length;
        setUsersTotal(Number(t) || arr.length);
      })
      .catch((e) => setErrorUsers(e?.message || "Failed to fetch"))
      .finally(() => setLoadingUsers(false));
  };

  const loadDrivers = (page = 1) => {
    setLoadingDrivers(true);
    fetch(buildUrl(API_DRIVERS, { page, per_page: PAGE_SIZE }))
      .then((r) => r.json())
      .then((data) => {
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        setDrivers(arr);
        const t = data?.meta?.total ?? data?.total ?? arr.length;
        setDriversTotal(Number(t) || arr.length);
      })
      .catch((e) => setErrorDrivers(e?.message || "Failed to fetch"))
      .finally(() => setLoadingDrivers(false));
  };

  const loadMerchants = async () => {
    setLoadingMerchants(true);
    try {
      const arr = await fetchAllRows(API_MERCHANTS);
      const normalized = arr.map((m) => ({
        ...m,
        owner_type: (m.owner_type || "").toLowerCase(),
      }));
      setMerchantsFood(normalized.filter((m) => m.owner_type === "food"));
      setMerchantsMart(normalized.filter((m) => m.owner_type === "mart"));
      setMerchantsEvents(
        normalized.filter((m) =>
          ["events", "event", "entertainment"].includes(m.owner_type)
        )
      );
      setMerchantsDining(
        normalized.filter((m) =>
          ["dining", "dining_out", "diningout", "restaurant"].includes(
            m.owner_type
          )
        )
      );
      setMerchantsHotel(
        normalized.filter((m) =>
          ["hotel", "hotels", "lodging"].includes(m.owner_type)
        )
      );
    } catch (e) {
      setErrorMerchants(e?.message || "Failed to fetch");
    } finally {
      setLoadingMerchants(false);
    }
  };

  useEffect(() => {
    const missing = [];
    if (!API_USERS) missing.push("REACT_APP_USERS_API_URL");
    if (!API_DRIVERS) missing.push("REACT_APP_DRIVERS_API_URL");
    if (!API_MERCHANTS) missing.push("REACT_APP_MERCHANTS_API_URL");

    if (missing.length) {
      const msg = `Missing env: ${missing.join(", ")}`;
      setErrorUsers(msg);
      setErrorDrivers(msg);
      setErrorMerchants(msg);
      return;
    }

    loadMerchants();

    // Fetch Event Organisers from Events API
    if (API_EVENTS_ORGANIZERS) {
      setLoadingOrganizers(true);
      const token = localStorage.getItem("access_token") || "";
      fetch(API_EVENTS_ORGANIZERS, {
        headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        cache: "no-store",
      })
        .then((r) => r.json())
        .then((json) => {
          const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
          setEventOrganizers(arr);
        })
        .catch(() => setEventOrganizers([]))
        .finally(() => setLoadingOrganizers(false));
    }
  }, []); // eslint-disable-line

  const filteredDrivers = useMemo(
    () => filterRows(drivers, query, "drivers"),
    [drivers, query]
  );
  const filteredMerchantsF = useMemo(
    () => filterRows(merchantsFood, query, "merchants"),
    [merchantsFood, query]
  );
  const filteredMerchantsM = useMemo(
    () => filterRows(merchantsMart, query, "merchants"),
    [merchantsMart, query]
  );
  const filteredMerchantsEvents = useMemo(
    () => filterRows(merchantsEvents, query, "merchants"),
    [merchantsEvents, query]
  );
  const filteredMerchantsDining = useMemo(
    () => filterRows(merchantsDining, query, "merchants"),
    [merchantsDining, query]
  );
  const filteredMerchantsHotel = useMemo(
    () => filterRows(merchantsHotel, query, "merchants"),
    [merchantsHotel, query]
  );
  const filteredCustomers = useMemo(
    () => filterRows(users, query, "customers"),
    [users, query]
  );

  const searchRowsAll = useMemo(() => {
    if (!query) return [];
    const mapDriver = filteredDrivers.map((r) => {
      const v = (r.vehicles && r.vehicles[0]) || {};
      const veh = [v.make, v.color, v.license_plate, v.vehicle_type]
        .filter(Boolean)
        .join(" • ");
      return {
        ...r,
        kind: "Driver",
        extra: veh || r.license_number || "",
      };
    });
    const mapFood = filteredMerchantsF.map((r) => ({
      ...r,
      kind: "Merchant (Food)",
      extra: r.business_name || "",
    }));
    const mapMart = filteredMerchantsM.map((r) => ({
      ...r,
      kind: "Shopkeeper (Mart)",
      extra: r.business_name || "",
    }));
    const mapEvents = filteredMerchantsEvents.map((r) => ({
      ...r,
      kind: "Events & Entertainment Partner",
      extra: r.business_name || "",
    }));
    const mapDining = filteredMerchantsDining.map((r) => ({
      ...r,
      kind: "Dining Out Partner",
      extra: r.business_name || "",
    }));
    const mapHotel = filteredMerchantsHotel.map((r) => ({
      ...r,
      kind: "Hotel Partner",
      extra: r.business_name || "",
    }));
    const mapCust = filteredCustomers.map((r) => ({
      ...r,
      kind: "Customer",
      extra: "",
    }));
    return [
      ...mapDriver,
      ...mapFood,
      ...mapMart,
      ...mapEvents,
      ...mapDining,
      ...mapHotel,
      ...mapCust,
    ];
  }, [
    query,
    filteredDrivers,
    filteredMerchantsF,
    filteredMerchantsM,
    filteredMerchantsEvents,
    filteredMerchantsDining,
    filteredMerchantsHotel,
    filteredCustomers,
  ]);

  useEffect(() => setSearchPage(1), [query]);

  useEffect(() => { if (API_USERS) loadUsers(customerPage); }, [customerPage]); // eslint-disable-line
  useEffect(() => { if (API_DRIVERS) loadDrivers(driverPage); }, [driverPage]); // eslint-disable-line

  const slice = (rows, page) =>
    rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageSearch = slice(searchRowsAll, searchPage);

  const pageOrganizers = slice(eventOrganizers, eventOrganizersPage);
  const pageMerchF = slice(merchantsFood, merchFoodPage);
  const pageMerchM = slice(merchantsMart, merchMartPage);
  const pageMerchE = slice(merchantsEvents, merchEventsPage);
  const pageMerchD = slice(merchantsDining, merchDiningPage);
  const pageMerchH = slice(merchantsHotel, merchHotelPage);

  // ---------- Actions ----------
  function getTargetUserId(row) {
    const raw = row.user_id ?? row.id;
    const s = String(raw ?? "");
    if (/^\d+$/.test(s)) return s;
    const firstNumber = s.match(/\d+/)?.[0];
    if (firstNumber) return firstNumber;
    return s.replace(/[^0-9a-zA-Z_-]/g, "");
  }

  const resolveActionUrl = (tpl, userId) => {
    const id = encodeURIComponent(String(userId));
    let url = tpl || "";
    if (url.includes(":user_id")) url = url.replace(":user_id", id);
    if (!url.endsWith(`/${id}`) && !url.includes(`/${id}?`)) {
      url = `${url.replace(/\/+$/, "")}/${id}`;
    }
    url = url.replace(/:(\w+)/g, "$1");
    return url;
  };

  const authToken = localStorage.getItem("access_token") || "";

  async function sendJSON({ url, method, body }) {
    const headers = { "Content-Type": "application/json" };
    if (authToken) headers.Authorization = `Bearer ${authToken}`;
    const res = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.message || msg;
      } catch {}
      const err = new Error(msg);
      err.status = res.status;
      throw err;
    }
    try {
      return await res.json();
    } catch {
      return { success: true };
    }
  }

  const refreshAll = () => {
    loadUsers(customerPage);
    loadDrivers(driverPage);
    loadMerchants();
  };

  const ensureEndpointOrAlert = (url, which) => {
    if (url) return true;
    Swal.fire({
      icon: "error",
      title: "Missing endpoint",
      text: `Missing ${which} endpoint in .env`,
    });
    return false;
  };

  async function writeAdminLog({ madeActive, targetId, targetName }) {
    if (!API_LOGS_CREATE_BASE) return;

    const admin_name = getAdminName();
    const token = localStorage.getItem("access_token") || "";
    const activity = `${madeActive ? "Activated" : "Deactivated"} user "${(
      targetName || ""
    ).trim()}" (id: ${targetId})`;

    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const payload = { user_id: String(targetId), admin_name, activity };

    const bases = [
      API_LOGS_CREATE_BASE.replace(/\/+$/, ""),
      `${API_LOGS_CREATE_BASE.replace(/\/+$/, "")}/create`,
      `${API_LOGS_CREATE_BASE.replace(/\/+$/, "")}/add`,
    ];

    for (const url of bases) {
      try {
        const res = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });
        if (res.ok) return;
      } catch (_) {}
    }

    console.warn("[Users.jsx] Failed to write admin log:", bases, payload);
  }

  async function sendUserNotification({ channel, targetUserId, title, message }) {
    if (!ensureEndpointOrAlert(API_NOTIFY_BASE, "notification")) return;
    const admin_id = getAdminId();
    const admin_name = getAdminName();

    const base = API_NOTIFY_BASE.replace(/\/+$/, "");
    const url = `${base}/${channel}`;

    await sendJSON({
      url,
      method: "POST",
      body: {
        user_id: admin_id,
        user_name: admin_name,
        target_user_id: targetUserId,
        title,
        message,
      },
    });
  }

  async function showSendNotification(row) {
    const targetUserId = getTargetUserId(row);
    if (!targetUserId) return Swal.fire({ icon: "error", title: "Missing user_id" });

    const { value: channel } = await Swal.fire({
      icon: "question",
      title: "Send notification",
      input: "radio",
      inputOptions: { sms: "SMS", email: "Email" },
      inputValue: "sms",
      confirmButtonText: "Next",
      showCancelButton: true,
    });
    if (!channel) return;

    const result = await Swal.fire({
      title: `${channel.toUpperCase()} Notification`,
      html:
        '<div class="swal-notify__form">' +
          '<label class="swal-notify__label" for="swal-title">Title</label>' +
          '<input id="swal-title" class="swal2-input swal-notify__input" placeholder="Enter title">' +
          '<label class="swal-notify__label" for="swal-message">Message</label>' +
          '<textarea id="swal-message" class="swal2-textarea swal-notify__textarea" placeholder="Write message"></textarea>' +
        '</div>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Send",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "swal-notify",
        title: "swal-notify__title",
        confirmButton: "swal-notify__confirm",
        cancelButton: "swal-notify__cancel",
        input: "swal-notify__input",
      },
      preConfirm: () => {
        const title = document.getElementById("swal-title")?.value?.trim() || "";
        const message = document.getElementById("swal-message")?.value?.trim() || "";
        if (!title || !message) {
          Swal.showValidationMessage("Title and message are required");
          return null;
        }
        return { title, message };
      },
    });

    if (!result.value) return;

    try {
      await sendUserNotification({
        channel,
        targetUserId,
        title: result.value.title,
        message: result.value.message,
      });
      Swal.fire({
        icon: "success",
        title: "Notification sent",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Send failed", text: e.message });
    }
  }

  async function showNotificationLogs(row) {
    const targetUserId = getTargetUserId(row);
    if (!targetUserId) return Swal.fire({ icon: "error", title: "Missing user_id" });
    if (!ensureEndpointOrAlert(API_NOTIFY_LOGS_BASE, "notification logs")) return;

    const token = localStorage.getItem("access_token") || "";
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const base = API_NOTIFY_LOGS_BASE.replace(/\/+$/, "");
    const url = `${base}/${encodeURIComponent(String(targetUserId))}`;

    try {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const rows = Array.isArray(json?.data) ? json.data : [];
      if (!rows.length) {
        return Swal.fire({
          icon: "info",
          title: "No recent notifications",
          text: `User ID: ${targetUserId}`,
        });
      }

      const html = rows
        .map((r) => {
          const when = r.created_at
            ? new Date(Number(r.created_at)).toLocaleString()
            : "—";
          return `
            <div style="padding:10px 0;border-bottom:1px solid #eee;">
              <div style="font-weight:600;">${r.title || "—"}</div>
              <div style="font-size:12px;color:#6b7280;margin:2px 0;">
                ${String(r.channel || "").toUpperCase()} • ${when}
              </div>
              <div style="white-space:pre-wrap;">${r.message || ""}</div>
            </div>
          `;
        })
        .join("");

      Swal.fire({
        title: "Recent notifications",
        html: `<div style="max-height:420px;overflow:auto;text-align:left;">${html}</div>`,
        width: 620,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed to load", text: e.message });
    }
  }

  async function sendAutoStatusNotification(row, madeActive) {
    if (!API_NOTIFY_BASE) return;
    const targetUserId = getTargetUserId(row);
    if (!targetUserId) return;
    const name = row.user_name || row.name || "";
    const title = madeActive ? "Account Activated" : "Account Deactivated";
    const message = madeActive
      ? `Hello ${name || "user"}, your account has been activated. Enjoy the service we provide.`
      : `Hello ${name || "user"}, your account has been deactivated. Please visit the TabDhey website to contact us if this is a mistake.`;
    try {
      await sendUserNotification({
        channel: "sms",
        targetUserId,
        title,
        message,
      });
    } catch (e) {
      console.warn("[Users.jsx] Auto notify failed:", e?.message || e);
    }
  }

  const handleToggleActive = async (row) => {
    const id = getTargetUserId(row);
    if (!id) return Swal.fire({ icon: "error", title: "Missing user_id" });

    const active = !!row.is_active;
    const endpointTpl = active ? API_ADMIN_DEACTIVATE : API_ADMIN_ACTIVATE;
    if (!ensureEndpointOrAlert(endpointTpl, active ? "deactivate" : "activate"))
      return;

    const url = resolveActionUrl(endpointTpl, id);
    const admin_id = getAdminId();
    const admin_name = getAdminName();

    const confirm = await Swal.fire({
      icon: "question",
      title: `${active ? "Deactivate" : "Activate"} user?`,
      text: `User ID: ${id}`,
      showCancelButton: true,
      confirmButtonText: active ? "Yes, deactivate" : "Yes, activate",
    });
    if (!confirm.isConfirmed) return;

    try {
      await sendJSON({
        url,
        method: "POST",
        body: { user_id: id, admin_id, admin_name },
      });
      await sendAutoStatusNotification(row, !active);
      writeAdminLog({
        madeActive: !active,
        targetId: id,
        targetName: row.user_name || row.name || row.email || "",
      });
      await Swal.fire({
        icon: "success",
        title: active ? "Deactivated" : "Activated",
        timer: 1200,
        showConfirmButton: false,
      });
      refreshAll();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Action failed", text: e.message });
    }
  };

  const ActionButtons = ({ row }) => {
    const isActive = !!row.is_active;
    return (
      <div className="actions-row">
        <button
          type="button"
          className="btn-action btn-message"
          title="Send notification"
          onClick={() => showSendNotification(row)}
          aria-label="Send notification"
        >
          <i className="bi bi-chat-dots-fill" />
        </button>
        <button
          type="button"
          className="btn-action btn-eye"
          title="View recent notifications"
          onClick={() => showNotificationLogs(row)}
          aria-label="View recent notifications"
        >
          <i className="bi bi-eye-fill" />
        </button>
        <button
          type="button"
          className={`btn-action ${isActive ? "btn-deactivate" : "btn-activate"}`}
          title={isActive ? "Deactivate" : "Activate"}
          onClick={() => handleToggleActive(row)}
          aria-label={isActive ? "Deactivate user" : "Activate user"}
        >
          <i
            className={`bi ${
              isActive ? "bi-person-dash-fill" : "bi-person-check-fill"
            }`}
          />
        </button>
      </div>
    );
  };

  /* ================== Columns (Wallet ID & Average Rating) ================== */
  const searchCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => i + 1 + (searchPage - 1) * PAGE_SIZE,
      },
      {
        header: "Type",
        render: (r) => <span className="type-pill">{r.kind}</span>,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.profile_image} name={r.user_name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">
            {r.user_name?.trim() || "-"}
          </div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{getWalletId(r)}</span>,
      },
      { header: "Email", key: "email" },
      { header: "Phone", key: "phone" },
      {
        header: "Vehicle",
        key: "extra",
        render: (r) => <div className="cell-wrap">{r.extra || ""}</div>,
      },
      {
        header: "Average Rating",
        render: (r) => (
          <span className="mono">{getAverageRating(r)}</span>
        ),
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.is_verified} />,
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={r} />,
      },
    ],
    [searchPage]
  );

  const driverCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => i + 1 + (driverPage - 1) * PAGE_SIZE,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.profile_image} name={r.user_name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">
            {r.user_name?.trim() || "-"}
          </div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{getWalletId(r)}</span>,
      },
      { header: "Email", key: "email" },
      { header: "Phone", key: "phone" },
      { header: "License", key: "license_number" },
      {
        header: "Vehicle",
        render: (r) => {
          const v = (r.vehicles && r.vehicles[0]) || {};
          const parts = [v.make, v.color, v.license_plate, v.vehicle_type].filter(Boolean);
          return parts.length ? parts.join(" • ") : "-";
        },
      },
      {
        header: "Average Rating",
        render: (r) => <span className="mono">{getAverageRating(r)}</span>,
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.is_verified} />,
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={r} />,
      },
    ],
    [driverPage]
  );

  const eventOrganizerCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => i + 1 + (eventOrganizersPage - 1) * PAGE_SIZE,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.user?.profile_image || r.profile_image || ""} name={r.name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">{r.name?.trim() || "—"}</div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{r.user?.wallet_id || "—"}</span>,
      },
      { header: "Email", render: (r) => r.user?.email || "—" },
      { header: "Phone", render: (r) => r.user?.phone || "—" },
      {
        header: "Total Events",
        render: (r) => (
          <span className="mono">{Number(r.event_count || 0).toLocaleString()}</span>
        ),
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.user?.is_verified} />,
      },
      {
        header: "Created At",
        render: (r) => r.created_at
          ? new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
          : "—",
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={{ ...r, user_id: r.user?.user_id ?? r.id, user_name: r.name, is_active: r.user?.is_active ?? r.is_active }} />,
      },
    ],
    [eventOrganizersPage]
  );

  const merchantFoodCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => i + 1 + (merchFoodPage - 1) * PAGE_SIZE,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.profile_image} name={r.user_name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">
            {r.user_name?.trim() || "-"}
          </div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{getWalletId(r)}</span>,
      },
      { header: "Email", key: "email" },
      { header: "Phone", key: "phone" },
      {
        header: "Business",
        key: "business_name",
        render: (r) => (
          <div className="cell-wrap">{r.business_name || ""}</div>
        ),
      },
      {
        header: "Average Rating",
        render: (r) => <span className="mono">{getAverageRating(r)}</span>,
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.is_verified} />,
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={r} />,
      },
    ],
    [merchFoodPage]
  );

  const merchantMartCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => i + 1 + (merchMartPage - 1) * PAGE_SIZE,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.profile_image} name={r.user_name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">
            {r.user_name?.trim() || "-"}
          </div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{getWalletId(r)}</span>,
      },
      { header: "Email", key: "email" },
      { header: "Phone", key: "phone" },
      {
        header: "Business",
        key: "business_name",
        render: (r) => (
          <div className="cell-wrap">{r.business_name || ""}</div>
        ),
      },
      {
        header: "Average Rating",
        render: (r) => <span className="mono">{getAverageRating(r)}</span>,
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.is_verified} />,
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={r} />,
      },
    ],
    [merchMartPage]
  );

  const merchantEventsCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => i + 1 + (merchEventsPage - 1) * PAGE_SIZE,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.profile_image} name={r.user_name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">
            {r.user_name?.trim() || "-"}
          </div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{getWalletId(r)}</span>,
      },
      { header: "Email", key: "email" },
      { header: "Phone", key: "phone" },
      {
        header: "Business",
        key: "business_name",
        render: (r) => (
          <div className="cell-wrap">{r.business_name || ""}</div>
        ),
      },
      {
        header: "Average Rating",
        render: (r) => <span className="mono">{getAverageRating(r)}</span>,
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.is_verified} />,
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={r} />,
      },
    ],
    [merchEventsPage]
  );

  const merchantDiningCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => i + 1 + (merchDiningPage - 1) * PAGE_SIZE,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.profile_image} name={r.user_name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">
            {r.user_name?.trim() || "-"}
          </div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{getWalletId(r)}</span>,
      },
      { header: "Email", key: "email" },
      { header: "Phone", key: "phone" },
      {
        header: "Business",
        key: "business_name",
        render: (r) => (
          <div className="cell-wrap">{r.business_name || ""}</div>
        ),
      },
      {
        header: "Average Rating",
        render: (r) => <span className="mono">{getAverageRating(r)}</span>,
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.is_verified} />,
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={r} />,
      },
    ],
    [merchDiningPage]
  );

  const merchantHotelCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => i + 1 + (merchHotelPage - 1) * PAGE_SIZE,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.profile_image} name={r.user_name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">
            {r.user_name?.trim() || "-"}
          </div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{getWalletId(r)}</span>,
      },
      { header: "Email", key: "email" },
      { header: "Phone", key: "phone" },
      {
        header: "Business",
        key: "business_name",
        render: (r) => (
          <div className="cell-wrap">{r.business_name || ""}</div>
        ),
      },
      {
        header: "Average Rating",
        render: (r) => <span className="mono">{getAverageRating(r)}</span>,
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.is_verified} />,
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={r} />,
      },
    ],
    [merchHotelPage]
  );

  const customerCols = useMemo(
    () => [
      {
        header: "SI.No",
        render: (_r, i) => (customerPage - 1) * PAGE_SIZE + i + 1,
      },
      {
        header: "Image",
        render: (r) => <Avatar src={r.profile_image} name={r.user_name} />,
      },
      {
        header: "Name",
        render: (r) => (
          <div className="cell-strong cell-wrap">
            {r.user_name?.trim() || "-"}
          </div>
        ),
      },
      {
        header: "Wallet ID",
        render: (r) => <span className="mono">{getWalletId(r)}</span>,
      },
      { header: "Email", key: "email" },
      { header: "Phone", key: "phone" },
      {
        header: "Points Collected",
        render: (r) => (
          <span className="mono">{getPointsCollected(r)}</span>
        ),
      },
      {
        header: "Status",
        render: (r) => <StatusPill verified={!!r.is_verified} />,
      },
      {
        header: "Actions",
        render: (r) => <ActionButtons row={r} />,
      },
    ],
    [customerPage]
  );

  /* ---------- EXPORTS ---------- */
  const exportDrivers = async () => {
    setExportOpen(false);
    setExporting(true);
    try {
      const all = await fetchAllRows(API_DRIVERS);
      const cols = [
        { header: "SI.No", getter: (_r, i) => i + 1 },
        { header: "Name", getter: (r) => r.user_name || "" },
        { header: "Wallet ID", getter: (r) => getWalletId(r) },
        { header: "Email", getter: (r) => r.email || "" },
        { header: "Phone", getter: (r) => r.phone || "" },
        { header: "License", getter: (r) => r.license_number || "" },
        {
          header: "Vehicle",
          getter: (r) => {
            const v = (r.vehicles && r.vehicles[0]) || {};
            return [v.make, v.color, v.license_plate,v.vehicle_type]
              .filter(Boolean)
              .join(" ");
          },
        },
        {
          header: "Average Rating",
          getter: (r) => getAverageRating(r),
        },
        {
          header: "Online",
          getter: (r) => (!!r.is_verified ? "Online" : "Offline"),
        },
        {
          header: "Active",
          getter: (r) => (!!r.is_active ? "Yes" : "No"),
        },
      ];
      exportTableToPDF({
        title: "Transport — Drivers",
        columns: cols,
        rows: all,
      });
    } finally {
      setExporting(false);
    }
  };

  const exportMerchantsFood = async () => {
    setExportOpen(false);
    setExporting(true);
    try {
      const all = await fetchAllRows(API_MERCHANTS);
      const food = (Array.isArray(all) ? all : [])
        .map((m) => ({
          ...m,
          owner_type: (m.owner_type || "").toLowerCase(),
        }))
        .filter((m) => m.owner_type === "food");
      const cols = [
        { header: "SI.No", getter: (_r, i) => i + 1 },
        { header: "Name", getter: (r) => r.user_name || "" },
        { header: "Wallet ID", getter: (r) => getWalletId(r) },
        { header: "Email", getter: (r) => r.email || "" },
        { header: "Phone", getter: (r) => r.phone || "" },
        { header: "Business", getter: (r) => r.business_name || "" },
        {
          header: "Average Rating",
          getter: (r) => getAverageRating(r),
        },
        {
          header: "Online",
          getter: (r) => (!!r.is_verified ? "Online" : "Offline"),
        },
        {
          header: "Active",
          getter: (r) => (!!r.is_active ? "Yes" : "No"),
        },
      ];
      exportTableToPDF({
        title: "Food — Merchants",
        columns: cols,
        rows: food,
      });
    } finally {
      setExporting(false);
    }
  };

  const exportMerchantsMart = async () => {
    setExportOpen(false);
    setExporting(true);
    try {
      const all = await fetchAllRows(API_MERCHANTS);
      const mart = (Array.isArray(all) ? all : [])
        .map((m) => ({
          ...m,
          owner_type: (m.owner_type || "").toLowerCase(),
        }))
        .filter((m) => m.owner_type === "mart");
      const cols = [
        { header: "SI.No", getter: (_r, i) => i + 1 },
        { header: "Name", getter: (r) => r.user_name || "" },
        { header: "Wallet ID", getter: (r) => getWalletId(r) },
        { header: "Email", getter: (r) => r.email || "" },
        { header: "Phone", getter: (r) => r.phone || "" },
        { header: "Business", getter: (r) => r.business_name || "" },
        {
          header: "Average Rating",
          getter: (r) => getAverageRating(r),
        },
        {
          header: "Online",
          getter: (r) => (!!r.is_verified ? "Online" : "Offline"),
        },
        {
          header: "Active",
          getter: (r) => (!!r.is_active ? "Yes" : "No"),
        },
      ];
      exportTableToPDF({
        title: "Mart — Shopkeepers",
        columns: cols,
        rows: mart,
      });
    } finally {
      setExporting(false);
    }
  };

  const exportMerchantsEvents = async () => {
    setExportOpen(false);
    setExporting(true);
    try {
      const all = await fetchAllRows(API_MERCHANTS);
      const events = (Array.isArray(all) ? all : [])
        .map((m) => ({
          ...m,
          owner_type: (m.owner_type || "").toLowerCase(),
        }))
        .filter((m) =>
          ["events", "event", "entertainment"].includes(m.owner_type)
        );
      const cols = [
        { header: "SI.No", getter: (_r, i) => i + 1 },
        { header: "Name", getter: (r) => r.user_name || "" },
        { header: "Wallet ID", getter: (r) => getWalletId(r) },
        { header: "Email", getter: (r) => r.email || "" },
        { header: "Phone", getter: (r) => r.phone || "" },
        { header: "Business", getter: (r) => r.business_name || "" },
        {
          header: "Average Rating",
          getter: (r) => getAverageRating(r),
        },
        {
          header: "Online",
          getter: (r) => (!!r.is_verified ? "Online" : "Offline"),
        },
        {
          header: "Active",
          getter: (r) => (!!r.is_active ? "Yes" : "No"),
        },
      ];
      exportTableToPDF({
        title: "Events & Entertainment — Partners",
        columns: cols,
        rows: events,
      });
    } finally {
      setExporting(false);
    }
  };

  const exportMerchantsDining = async () => {
    setExportOpen(false);
    setExporting(true);
    try {
      const all = await fetchAllRows(API_MERCHANTS);
      const dining = (Array.isArray(all) ? all : [])
        .map((m) => ({
          ...m,
          owner_type: (m.owner_type || "").toLowerCase(),
        }))
        .filter((m) =>
          ["dining", "dining_out", "diningout", "restaurant"].includes(
            m.owner_type
          )
        );
      const cols = [
        { header: "SI.No", getter: (_r, i) => i + 1 },
        { header: "Name", getter: (r) => r.user_name || "" },
        { header: "Wallet ID", getter: (r) => getWalletId(r) },
        { header: "Email", getter: (r) => r.email || "" },
        { header: "Phone", getter: (r) => r.phone || "" },
        { header: "Business", getter: (r) => r.business_name || "" },
        {
          header: "Average Rating",
          getter: (r) => getAverageRating(r),
        },
        {
          header: "Online",
          getter: (r) => (!!r.is_verified ? "Online" : "Offline"),
        },
        {
          header: "Active",
          getter: (r) => (!!r.is_active ? "Yes" : "No"),
        },
      ];
      exportTableToPDF({
        title: "Dining Out — Partners",
        columns: cols,
        rows: dining,
      });
    } finally {
      setExporting(false);
    }
  };

  const exportMerchantsHotel = async () => {
    setExportOpen(false);
    setExporting(true);
    try {
      const all = await fetchAllRows(API_MERCHANTS);
      const hotel = (Array.isArray(all) ? all : [])
        .map((m) => ({
          ...m,
          owner_type: (m.owner_type || "").toLowerCase(),
        }))
        .filter((m) =>
          ["hotel", "hotels", "lodging"].includes(m.owner_type)
        );
      const cols = [
        { header: "SI.No", getter: (_r, i) => i + 1 },
        { header: "Name", getter: (r) => r.user_name || "" },
        { header: "Wallet ID", getter: (r) => getWalletId(r) },
        { header: "Email", getter: (r) => r.email || "" },
        { header: "Phone", getter: (r) => r.phone || "" },
        { header: "Business", getter: (r) => r.business_name || "" },
        {
          header: "Average Rating",
          getter: (r) => getAverageRating(r),
        },
        {
          header: "Online",
          getter: (r) => (!!r.is_verified ? "Online" : "Offline"),
        },
        {
          header: "Active",
          getter: (r) => (!!r.is_active ? "Yes" : "No"),
        },
      ];
      exportTableToPDF({
        title: "Hotel — Partners",
        columns: cols,
        rows: hotel,
      });
    } finally {
      setExporting(false);
    }
  };

  const exportCustomers = async () => {
    setExportOpen(false);
    setExporting(true);
    try {
      const all = await fetchAllRows(API_USERS);
      const cols = [
        { header: "SI.No", getter: (_r, i) => i + 1 },
        { header: "Name", getter: (r) => r.user_name || "" },
        { header: "Wallet ID", getter: (r) => getWalletId(r) },
        { header: "Email", getter: (r) => r.email || "" },
        { header: "Phone", getter: (r) => r.phone || "" },
        {
          header: "Points Collected",
          getter: (r) => getPointsCollected(r),
        },
        {
          header: "Online",
          getter: (r) => (!!r.is_verified ? "Online" : "Offline"),
        },
        {
          header: "Active",
          getter: (r) => (!!r.is_active ? "Yes" : "No"),
        },
      ];
      exportTableToPDF({
        title: "General Users — Customers",
        columns: cols,
        rows: all,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container-fluid px-4 users-page">
      {/* ---------- Page toolbar (search + export) ---------- */}
      <div className="users-toolbar">
        <div className="toolbar-left">
          <div className="search-wrap">
            <input
              className="search-input"
              type="text"
              placeholder="Search across Drivers, Merchants, Shopkeepers, Customers…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search"
            />
            {query && (
              <button
                className="clear-btn"
                aria-label="Clear search"
                onClick={() => setQuery("")}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="toolbar-right" ref={exportWrapRef}>
          <button
            type="button"
            className="export-btn"
            onClick={() => !exporting && setExportOpen((v) => !v)}
            disabled={exporting}
            aria-busy={exporting}
          >
            {exporting ? "Preparing…" : "Export ▼"}
          </button>

          {exportOpen && (
            <div className="export-menu">
              <button className="export-item" onClick={exportDrivers}>
                Transport — Drivers (PDF) • all
              </button>
              <button className="export-item" onClick={exportMerchantsFood}>
                Food — Merchants (PDF) • all
              </button>
              <button className="export-item" onClick={exportMerchantsMart}>
                Mart — Shopkeepers (PDF) • all
              </button>
              <button className="export-item" onClick={exportMerchantsEvents}>
                Events &amp; Entertainment — Partners (PDF) • all
              </button>
              <button className="export-item" onClick={exportMerchantsDining}>
                Dining Out — Partners (PDF) • all
              </button>
              <button className="export-item" onClick={exportMerchantsHotel}>
                Hotel — Partners (PDF) • all
              </button>
              <button className="export-item" onClick={exportCustomers}>
                General Users — Customers (PDF) • all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ---------- SEARCH RESULTS ---------- */}
      {query && (
        <section className="users-section">
          <div className="section-head">
            <h3>Search Results</h3>
            <small className="muted">
              {searchRowsAll.length} match
              {searchRowsAll.length === 1 ? "" : "es"} • across all categories
            </small>
          </div>
          <TableCard
            title="Matches"
            columns={searchCols}
            rows={pageSearch}
            loading={false}
            emptyText="No matches found"
            page={searchPage}
            pageSize={PAGE_SIZE}
            total={searchRowsAll.length}
            onPageChange={setSearchPage}
            // horizontal scroll only for this table
            scrollX
            tableMinWidth={1600}
          />
        </section>
      )}

      {/* Transport */}
      <section className="users-section">
        <div className="section-head">
          <h3>Transport</h3>
        </div>
        <TableCard
          title="Drivers"
          columns={driverCols}
          rows={drivers}
          loading={loadingDrivers}
          emptyText={errorDrivers || "No drivers found"}
          page={driverPage}
          pageSize={PAGE_SIZE}
          total={driversTotal}
          onPageChange={setDriverPage}
        />
      </section>

      {/* Food */}
      <section className="users-section">
        <div className="section-head">
          <h3>Food</h3>
        </div>
        <TableCard
          title="Food Merchants"
          columns={merchantFoodCols}
          rows={pageMerchF}
          loading={loadingMerchants}
          emptyText={errorMerchants || "No merchants yet"}
          page={merchFoodPage}
          pageSize={PAGE_SIZE}
          total={merchantsFood.length}
          onPageChange={setMerchFoodPage}
        />
      </section>

      {/* Mart */}
      <section className="users-section">
        <div className="section-head">
          <h3>Mart</h3>
        </div>
        <TableCard
          title="Mart Merchants"
          columns={merchantMartCols}
          rows={pageMerchM}
          loading={loadingMerchants}
          emptyText={errorMerchants || "No shopkeepers yet"}
          page={merchMartPage}
          pageSize={PAGE_SIZE}
          total={merchantsMart.length}
          onPageChange={setMerchMartPage}
        />
      </section>

      {/* Events & Entertainment */}
      <section className="users-section">
        <div className="section-head">
          <h3>Events &amp; Entertainment</h3>
        </div>

        {/* Event Organisers from Events API */}
        <TableCard
          title="Event Organisers"
          columns={eventOrganizerCols}
          rows={pageOrganizers}
          loading={loadingOrganizers}
          emptyText="No event organisers found"
          page={eventOrganizersPage}
          pageSize={PAGE_SIZE}
          total={eventOrganizers.length}
          onPageChange={setEventOrganizersPage}
        />

        {/* Entertainment Partners from Merchants API (if any) */}
        {merchantsEvents.length > 0 && (
          <TableCard
            title="Entertainment Partners"
            columns={merchantEventsCols}
            rows={pageMerchE}
            loading={loadingMerchants}
            emptyText={errorMerchants || "No partners yet"}
            page={merchEventsPage}
            pageSize={PAGE_SIZE}
            total={merchantsEvents.length}
            onPageChange={setMerchEventsPage}
          />
        )}
      </section>

      {/* Dining Out */}
      <section className="users-section">
        <div className="section-head">
          <h3>Dining Out</h3>
        </div>
        <TableCard
          title="Dining Owners"
          columns={merchantDiningCols}
          rows={pageMerchD}
          loading={loadingMerchants}
          emptyText={errorMerchants || "No partners yet"}
          page={merchDiningPage}
          pageSize={PAGE_SIZE}
          total={merchantsDining.length}
          onPageChange={setMerchDiningPage}
        />
      </section>

      {/* Hotel */}
      <section className="users-section">
        <div className="section-head">
          <h3>Hotel</h3>
        </div>
        <TableCard
          title="Hotel Owners"
          columns={merchantHotelCols}
          rows={pageMerchH}
          loading={loadingMerchants}
          emptyText={errorMerchants || "No partners yet"}
          page={merchHotelPage}
          pageSize={PAGE_SIZE}
          total={merchantsHotel.length}
          onPageChange={setMerchHotelPage}
        />
      </section>

      {/* Customers */}
      <section className="users-section">
        <div className="section-head">
          <h3>General Users</h3>
        </div>
        <TableCard
          title="Customers"
          columns={customerCols}
          rows={users}
          loading={loadingUsers}
          emptyText={errorUsers || "No customers found"}
          page={customerPage}
          pageSize={PAGE_SIZE}
          total={usersTotal}
          onPageChange={setCustomerPage}
        />
      </section>

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All
          rights reserved
        </small>
      </footer>
    </div>
  );
}
